/**
 * SummaryChart_V2 - Total Consumption Summary Chart
 *
 * Displays 15-minute interval consumption data with:
 * - Color-coded bars (green = below avg price, red = above avg price)
 * - Metered consumption overlay (if available) with diagonal hatch pattern
 * - Spot price and day average lines
 * - Responsive design with mobile toggle for hourly aggregation
 * - InfoBox with daily summaries
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import { formatKWh, formatCelcius, formatCents } from '../ChartUtils';
import {
  extractTimeseries,
  aggregateToHourly,
  getHourlyTicks,
  formatHourLabel,
  hasMetering,
  getResponsiveBarSize,
  isMobileLayout
} from './heatingChartUtils';
import { t } from '../../i18n';

const SummaryChart_V2 = ({ data, metadata, isMobile: isMobileProp = null, showDetails = false }) => {
  console.log('SummaryChart_V2: Rendering with data:', data, 'metadata:', metadata);

  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Extract timeseries
  const timeseries = useMemo(() => {
    const result = extractTimeseries(data);
    console.log('SummaryChart_V2: Extracted timeseries:', result.length, 'entries');
    if (result.length === 0) {
      console.error('SummaryChart_V2: No timeseries entries extracted!');
    }
    return result;
  }, [data]);

  // Check if metering is available
  const hasMeteredData = useMemo(() => hasMetering(data), [data]);

  // Determine if mobile layout (use prop if provided, otherwise detect)
  const isMobile = useMemo(() => {
    return isMobileProp !== null ? isMobileProp : isMobileLayout(containerWidth);
  }, [isMobileProp, containerWidth]);

  // Process data for chart (15-min or hourly based on mobile + toggle)
  const processedData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateToHourly(timeseries) : timeseries;
    console.log('SummaryChart_V2: Processing', source.length, 'snapshots, shouldAggregate:', shouldAggregate);

    return source.map(snapshot => {
      // Use controlled consumption (Habitaio-controlled devices)
      const controlledConsumption = snapshot.consumption?.controlled || snapshot.consumption?.total || 0;

      // Use metered total consumption (whole household) if available
      const meteredTotal = snapshot.metered?.consumption?.total || null;

      // Calculate uncontrolled consumption (delta between metered and controlled)
      const uncontrolledConsumption = hasMeteredData && meteredTotal !== null
        ? Math.max(0, meteredTotal - controlledConsumption)
        : 0;

      return {
        timestamp: snapshot.timestamp,
        controlledConsumption: controlledConsumption,
        uncontrolledConsumption: uncontrolledConsumption,
        price: snapshot.pricing?.price || 0,
        dayAvg: snapshot.pricing?.day_avg || 0,
        belowAvg: !!snapshot.pricing?.below_avg,
        cost: snapshot.consumption?.cost || 0
      };
    });
  }, [timeseries, isMobile, showDetails, hasMeteredData]);

  // Get tick marks (hourly)
  const hourlyTicks = useMemo(() => {
    if (isMobile && !showDetails) {
      // Show fewer ticks on mobile hourly view
      return processedData
        .filter((_, index) => index % 2 === 0) // Every 2 hours
        .map(d => d.timestamp);
    }
    return getHourlyTicks(processedData);
  }, [processedData, isMobile, showDetails]);

  // Bar size based on container width
  const barSize = useMemo(() => {
    return getResponsiveBarSize(containerWidth);
  }, [containerWidth]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // Handle both metadata structures (flat and nested under totals)
  const totals = metadata.totals || metadata;
  const totalConsumption = totals.total_consumption || 0;
  const totalCost = totals.total_cost || 0;
  const consumptionBelowAvg = totals.consumption_below_avg;
  const consumptionAboveAvg = totals.consumption_above_avg;
  const realisedAvg = totals.realised_avg || 0;
  const spotAvg = totals.spot_avg || metadata.spot_avg || 0;
  const estimatedSaving = totals.estimated_saving || 0;
  const outdoorTempAvg = metadata.outdoor_temp_avg;

  // InfoBox data - structure depends on whether metered data is available
  const summaryDataPoints = useMemo(() => {
    if (hasMeteredData && metadata.metering_stats) {
      // Structure with metered data
      const meteringStats = metadata.metering_stats;
      const meteredConsumption = meteringStats.import || 0;
      const meteredCost = meteringStats.cost || 0;

      return [
        { label: t('heatingChart.totalMeteredConsumption'), value: formatKWh(meteredConsumption) },
        { label: t('heatingChart.totalCost'), value: formatCents(meteredCost) },
        { label: t('heatingChart.realisedAvg'), value: `${(meteredCost / meteredConsumption).toFixed(3)} c/kWh` },
        { label: t('heatingChart.habitaioSmartControl'), value: '' }, // Section header
        { label: t('heatingChart.totalConsumption'), value: formatKWh(totalConsumption) },
        ...(consumptionBelowAvg !== undefined && consumptionAboveAvg !== undefined ?
          [{ label: t('heatingChart.consumptionBelowAboveAvg'),
             value: `${formatKWh(consumptionBelowAvg)} / ${formatKWh(consumptionAboveAvg)}` }]
          : []
        ),
        { label: t('heatingChart.totalCost'), value: formatCents(totalCost) },
        { label: t('heatingChart.realisedAvg'), value: `${realisedAvg} c/kWh` },
        { label: t('heatingChart.spotAvgFull'), value: `${spotAvg} c/kWh` },
        { label: t('heatingChart.estimatedSaving'), value: formatCents(estimatedSaving) },
        ...(outdoorTempAvg !== null && outdoorTempAvg !== undefined ?
          [{ label: t('heatingChart.outdoorTempAvg'), value: formatCelcius(outdoorTempAvg) }]
          : []
        ),
      ];
    } else {
      // Fallback structure without metered data
      return [
        { label: t('heatingChart.totalConsumption'), value: formatKWh(totalConsumption) },
        ...(consumptionBelowAvg !== undefined && consumptionAboveAvg !== undefined ?
          [{ label: t('heatingChart.consumptionBelowAboveAvg'),
             value: `${formatKWh(consumptionBelowAvg)} / ${formatKWh(consumptionAboveAvg)}` }]
          : []
        ),
        { label: t('heatingChart.totalCost'), value: formatCents(totalCost) },
        { label: t('heatingChart.spotAvgFull'), value: `${spotAvg} c/kWh` },
        { label: t('heatingChart.realisedAvg'), value: `${realisedAvg} c/kWh` },
        { label: t('heatingChart.estimatedSaving'), value: formatCents(estimatedSaving) },
        ...(outdoorTempAvg !== null && outdoorTempAvg !== undefined ?
          [{ label: t('heatingChart.outdoorTempAvg'), value: formatCelcius(outdoorTempAvg) }]
          : []
        ),
      ];
    }
  }, [hasMeteredData, metadata, totalConsumption, totalCost, consumptionBelowAvg, consumptionAboveAvg, realisedAvg, spotAvg, estimatedSaving, outdoorTempAvg]);

  // Custom bar shape for controlled consumption (green if below avg, red if above)
  const ControlledBarShape = (props) => {
    const { x, y, width, height, payload } = props;
    const fill = payload.belowAvg ? '#90ee90' : '#ff6961';
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  };

  // Custom legend
  const customLegend = [
    { value: t('heatingChart.totalConsumptionBelowAvg'), type: 'square', color: '#90ee90' },
    { value: t('heatingChart.totalConsumptionAboveAvg'), type: 'square', color: '#ff6961' },
    ...(hasMeteredData ? [
      { value: 'Uncontrolled Consumption', type: 'square', color: '#d3d3d3' }
    ] : []),
    { value: t('heatingChart.electricityPrice'), type: 'line', color: '#ff7300' },
    { value: t('heatingChart.dayAverage'), type: 'line', color: '#ffa089' }
  ];

  // Check for data issues after all hooks
  if (!data || !data.timeseries) {
    console.error('SummaryChart_V2: No data or timeseries available');
    return <div style={{ padding: '20px', color: 'red' }}>No chart data available</div>;
  }

  if (timeseries.length === 0) {
    console.error('SummaryChart_V2: Empty timeseries after extraction');
    return <div style={{ padding: '20px', color: 'red' }}>No timeseries data extracted</div>;
  }

  if (processedData.length === 0) {
    console.error('SummaryChart_V2: Empty processedData');
    return <div style={{ padding: '20px', color: 'red' }}>No processed data available</div>;
  }

  console.log('SummaryChart_V2: About to render chart with', processedData.length, 'data points');

  return (
    <>
      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={processedData}
            margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
          >
            <defs>
              {/* Diagonal hatch pattern for metered data */}
              <pattern
                id="diagonalHatch"
                patternUnits="userSpaceOnUse"
                width="4"
                height="4"
              >
                <path
                  d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"
                  stroke="#555"
                  strokeWidth="1.5"
                />
              </pattern>
            </defs>

            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              name="Hour"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
              height={40}
            />

            <YAxis
              yAxisId="left"
              label={{ value: t('heatingChart.consumption') + ' (kWh)', angle: -90, position: 'insideLeft' }}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: t('heatingChart.price') + ' (c/kWh)', angle: 90, position: 'insideRight' }}
            />

            <Tooltip
              formatter={(value, name) => {
                if (name.includes('Consumption') || name.includes('consumption')) {
                  return [formatKWh(value), name];
                }
                if (name.includes('Price') || name.includes('price')) {
                  return [`${Number(value).toFixed(2)} c/kWh`, name];
                }
                return [value, name];
              }}
            />

            <Legend payload={customLegend} />

            {/* Stacked bars: Bottom = Controlled (green/red), Top = Uncontrolled (gray) */}
            {/* Controlled consumption bar (bottom of stack, color-coded by price) */}
            <Bar
              yAxisId="left"
              dataKey="controlledConsumption"
              name={t('heatingChart.totalConsumption')}
              stackId="consumption"
              shape={<ControlledBarShape />}
              barSize={barSize}
            />

            {/* Uncontrolled consumption bar (top of stack, light gray) - only if metering available */}
            {hasMeteredData && (
              <Bar
                yAxisId="left"
                dataKey="uncontrolledConsumption"
                name="Uncontrolled Consumption"
                stackId="consumption"
                fill="#d3d3d3"
                barSize={barSize}
              />
            )}

            {/* Price lines */}
            <Line
              type="step"
              yAxisId="right"
              dataKey="price"
              name={t('heatingChart.electricityPrice')}
              stroke="#ff7300"
              strokeWidth={2}
              dot={false}
            />

            <Line
              yAxisId="right"
              dataKey="dayAvg"
              name={t('heatingChart.dayAverage')}
              stroke="#ffa089"
              strokeDasharray="8"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* InfoBox */}
      <div>
        <InfoBox title="Summary" dataPoints={summaryDataPoints} />
      </div>
    </>
  );
};

export default SummaryChart_V2;
